import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import Modal from '../components/common/Modal';
import { Pagination } from '../components/common/Pagination';
import { 
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { ClockIcon } from '@heroicons/react/24/solid';

interface LeaveRequest {
  id: number;
  userId: string;
  leaveType: {
    id: number;
    name: string;
    code: string;
  };
  startDate: string;
  endDate: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    department?: {
      name: string;
    };
  };
  workingDays?: number;
}

export const TeamLeaveRequests = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.roles?.some((role: any) => role.name?.toLowerCase() === 'admin');
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch team leave requests
  const { data: leaveRequestsData, isLoading } = useQuery({
    queryKey: ['teamLeaveRequests', currentPage, itemsPerPage],
    queryFn: async () => {
      const response = await api.get(`/leaves/team?page=${currentPage}&limit=${itemsPerPage}`);
      console.log('Team leave requests:', response.data);
      
      const requests = response.data.data || [];
      const mappedRequests = requests.map((request: any) => ({
        id: request.id,
        userId: request.user_id || request.userId,
        startDate: request.start_date || request.startDate,
        endDate: request.end_date || request.endDate,
        reason: request.reason,
        status: request.status,
        createdAt: request.created_at || request.createdAt,
        leaveType: request.leaveType || request.leave_type,
        user: {
          id: request.user?.id || request.user_id,
          firstName: request.user?.first_name || request.user?.firstName,
          lastName: request.user?.last_name || request.user?.lastName,
          email: request.user?.email,
          department: request.user?.department
        },
        workingDays: calculateWorkingDays(request.start_date || request.startDate, request.end_date || request.endDate)
      }));
      
      return {
        requests: mappedRequests,
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 0,
        currentPage: response.data.page || 1
      };
    }
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async (requestId: number) => {
      const response = await api.post(`/leaves/${requestId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamLeaveRequests'] });
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
    }
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: number, reason: string }) => {
      const response = await api.post(`/leaves/${requestId}/reject`, { reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamLeaveRequests'] });
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedRequestId(null);
    }
  });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = parseISO(dateString);
      return format(date, 'dd.MM.yyyy', { locale: pl });
    } catch (error) {
      console.error('Date formatting error:', error);
      return '-';
    }
  };

  const getStatusBadgeVariant = (status: string): 'warning' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Oczekujący';
      case 'approved':
        return 'Zatwierdzony';
      case 'rejected':
        return 'Odrzucony';
      case 'cancelled':
        return 'Anulowany';
      default:
        return status;
    }
  };

  const handleApprove = (requestId: number) => {
    if (confirm('Czy na pewno chcesz zatwierdzić ten wniosek?')) {
      approveMutation.mutate(requestId);
    }
  };

  const handleReject = (requestId: number) => {
    setSelectedRequestId(requestId);
    setShowRejectModal(true);
  };

  const confirmReject = () => {
    if (selectedRequestId && rejectReason.trim()) {
      rejectMutation.mutate({
        requestId: selectedRequestId,
        reason: rejectReason
      });
    }
  };

  const calculateWorkingDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;
    
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      let count = 0;
      const current = new Date(start);
      
      while (current <= end) {
        const dayOfWeek = current.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          count++;
        }
        current.setDate(current.getDate() + 1);
      }
      
      return count;
    } catch (error) {
      return 0;
    }
  };

  if (isLoading) return <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>;

  const leaveRequests = leaveRequestsData?.requests || [];
  const totalItems = leaveRequestsData?.total || 0;
  const totalPages = leaveRequestsData?.totalPages || 0;
  
  const pendingRequests = leaveRequests.filter(req => req.status === 'pending');
  const processedRequests = leaveRequests.filter(req => req.status !== 'pending');

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Wnioski urlopowe zespołu</h1>
        <p className="mt-1 text-sm text-gray-500">
          Zarządzaj wnioskami urlopowymi członków twojego zespołu
        </p>
      </div>

      {/* Pending Requests */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Oczekujące wnioski ({pendingRequests.length})
        </h2>
        {pendingRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
            Brak oczekujących wniosków
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {pendingRequests.map((request) => (
                <li key={request.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <p className="text-sm font-medium text-gray-900">
                            {request.user.firstName} {request.user.lastName}
                          </p>
                          <Badge variant="warning" className="ml-2">
                            {getStatusText(request.status)}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {formatDate(request.startDate)} - {formatDate(request.endDate)}
                            <span className="ml-2 font-medium">({request.workingDays} dni roboczych)</span>
                          </div>
                          <div className="text-sm text-gray-500">
                            Typ: <span className="font-medium">{request.leaveType?.name || 'Nieznany'}</span>
                          </div>
                        </div>
                        {request.reason && (
                          <p className="mt-2 text-sm text-gray-600">
                            Powód: {request.reason}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {(request.userId !== user?.id || isAdmin) && (
                          <>
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleApprove(request.id)}
                              disabled={approveMutation.isPending}
                            >
                              <CheckIcon className="h-4 w-4 mr-1" />
                              Zatwierdź
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleReject(request.id)}
                              disabled={rejectMutation.isPending}
                            >
                              <XMarkIcon className="h-4 w-4 mr-1" />
                              Odrzuć
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/leaves/${request.id}`)}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Processed Requests */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Historia wniosków ({processedRequests.length})
        </h2>
        {processedRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
            Brak przetworzonych wniosków
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {processedRequests.map((request) => (
                <li key={request.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <p className="text-sm font-medium text-gray-900">
                            {request.user.firstName} {request.user.lastName}
                          </p>
                          <Badge 
                            variant={getStatusBadgeVariant(request.status)} 
                            className="ml-2"
                          >
                            {getStatusText(request.status)}
                          </Badge>
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              <CalendarIcon className="h-4 w-4 mr-1" />
                              {formatDate(request.startDate)} - {formatDate(request.endDate)}
                            </p>
                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                              <ClockIcon className="h-4 w-4 mr-1" />
                              {formatDate(request.createdAt)}
                            </p>
                          </div>
                          <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                            {request.leaveType?.name || 'Nieznany'}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/leaves/${request.id}`)}
                        className="ml-4"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectReason('');
          setSelectedRequestId(null);
        }}
        title="Odrzuć wniosek"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Podaj powód odrzucenia wniosku:
          </p>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Wpisz powód odrzucenia..."
          />
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason('');
                setSelectedRequestId(null);
              }}
            >
              Anuluj
            </Button>
            <Button
              variant="danger"
              onClick={confirmReject}
              disabled={!rejectReason.trim() || rejectMutation.isPending}
            >
              Odrzuć wniosek
            </Button>
          </div>
        </div>
      </Modal>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={(page) => {
          setCurrentPage(page);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    </div>
  );
};